import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import Pagination from '@/components/base/Pagination';
import { movies, type Movie } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';
import FilterSidebar, { type CategoryFilters } from './components/FilterSidebar';

const PAGE_SIZE = 8;

const sortFns: Record<string, (a: Movie, b: Movie) => number> = {
  latest: (a, b) => b.year - a.year,
  heat: (a, b) => b.heat - a.heat,
  rating: (a, b) => b.rating - a.rating,
};

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filters: CategoryFilters = {
    type: searchParams.get('type') || '全部',
    region: searchParams.get('region') || '全部',
    year: searchParams.get('year') || '全部',
    sort: searchParams.get('sort') || 'latest',
  };
  const page = Math.max(1, Number(searchParams.get('page') || 1));

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === '全部' || (key === 'sort' && value === 'latest')) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    next.delete('page');
    setSearchParams(next);
  };

  const filtered = useMemo(() => {
    const list = movies.filter((m) => {
      if (filters.type !== '全部' && m.type !== filters.type) return false;
      if (filters.region !== '全部' && m.region !== filters.region) return false;
      if (filters.year !== '全部' && String(m.year) !== filters.year) return false;
      return true;
    });
    const sorter = sortFns[filters.sort] || sortFns.latest;
    return [...list].sort(sorter);
  }, [filters.type, filters.region, filters.year, filters.sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handlePage = (next: number) => {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete('page');
    else params.set('page', String(next));
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetFilters = () => {
    setSearchParams(new URLSearchParams());
    setDrawerOpen(false);
  };

  const description = `伊人在线影视分类页，当前筛选：${filters.type} · ${filters.region} · ${filters.year}，共 ${filtered.length} 部作品，支持按年份、地区、热度筛选与排序。`;
  const pageTitle = filters.type === '全部' ? '影视分类 - 伊人在线' : `${filters.type} - 影视分类 - 伊人在线`;

  usePageMeta({
    title: pageTitle,
    description,
    keywords: '影视分类,电视剧,电影,影视筛选,伊人在线',
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        <div className="border-b border-background-200/70 bg-background-100/60">
          <div className="mx-auto px-4 py-8 md:px-10 md:py-10">
            <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
              <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">影视分类</span>
              {filters.type !== '全部' && (
                <>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-accent-600">{filters.type}</span>
                </>
              )}
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              {filters.type === '全部' ? '全部影视' : filters.type}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600">
              汇集最新电视剧与电影作品，通过左侧筛选条件快速找到你想看的内容。
            </p>
          </div>
        </div>

        <div className="mx-auto flex flex-col gap-6 px-4 py-8 md:flex-row md:px-10 md:py-10">
          {/* Desktop sidebar */}
          <aside className="hidden w-[260px] flex-shrink-0 md:block">
            <div className="sticky top-24">
              <FilterSidebar
                filters={filters}
                onChange={(patch) => updateParams(patch as Record<string, string>)}
                onReset={resetFilters}
                resultCount={filtered.length}
              />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            {/* Mobile filter trigger */}
            <div className="mb-4 flex items-center justify-between md:hidden">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
              >
                <i className="ri-filter-3-line"></i>
                筛选 / 排序
              </button>
              <span className="text-xs text-foreground-500">共 {filtered.length} 部</span>
            </div>

            {paged.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg bg-background-100 py-20 text-center ring-1 ring-background-200/70">
                <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
                <p className="mt-3 text-sm text-foreground-700">暂无匹配影视</p>
                <p className="mt-1 text-xs text-foreground-500">换个筛选条件试试，或者重置全部条件</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-accent-950 transition-colors hover:bg-accent-600"
                >
                  重置筛选
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                  {paged.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} showHeat={filters.sort === 'heat'} />
                  ))}
                </div>
                <Pagination current={currentPage} total={totalPages} onChange={handlePage} />
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />

      {/* Mobile filter drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/45"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm overflow-y-auto bg-background-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-950">筛选 / 排序</span>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterSidebar
              filters={filters}
              onChange={(patch) => updateParams(patch as Record<string, string>)}
              onReset={resetFilters}
              resultCount={filtered.length}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}